import { Component, computed, inject, input, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Game } from '@app/shared/models/game.model';
import { TakeUntilDestroy } from '@shared/decorators/take-until-destroy.decorator';
import { User } from '@shared/models/user.model';
import { AssetsService } from '@shared/services/assets.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { Tag } from '@app/shared/models/assets.model';
import { TagsService } from '@app/shared/services/tags.service';
import { AppService } from '@app/app.service';
import { AnalyticsService } from '@app/shared/services/analytics.service';
import { SharedModule } from '@app/shared/shared.module';
import { GameTrophyProgressComponent } from '@app/home/trophies/game-trophy-progress/game-trophy-progress.component';
import { TooltipDirective } from '@app/shared/directives/tooltip.directive';
import { FirehoseAnalyticsService } from '@app/core/analytics/firehose/public-api';
import { UIRepository } from '@app/shared/state/ui/ui.repository';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-lobby-game',
  templateUrl: './lobby-game.component.html',
  styleUrls: ['./lobby-game.component.scss'],
  standalone: true,
  imports: [SharedModule, GameTrophyProgressComponent, TooltipDirective],
})
export class LobbyGameComponent {
  game = input.required<Game>();
  user = input.required<User>();
  tagsList = input.required<Tag[]>();
  gameUnlockFlip = input<boolean>(false);
  isMobileOrTablet = input<boolean>(false);
  isIconLoaded: WritableSignal<boolean> = signal(false);

  private readonly eventManager = inject(EventManager);
  public readonly logger = inject(LoggerService);
  private readonly assetsService = inject(AssetsService);
  private readonly tagsService = inject(TagsService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly appService = inject(AppService);
  private readonly uiRepo = inject(UIRepository);
  private readonly firehoseAnalyticsService = inject(FirehoseAnalyticsService);

  private gamesVersion = toSignal(this.assetsService.gamesVersion$, { initialValue: undefined });
  private maintenanceTag = signal(this.tagsService.getMaintenanceTag());
  private lockedTag = signal(this.tagsService.getLockedTag());
  private testTag = signal(this.tagsService.getTestTag());
  private tagsIndex = computed(() => {
    const list = this.tagsList() ?? [];
    return new Map(list.map((t) => [t.name, t]));
  });
  private firstTagName = computed(() => this.game()?.tags?.[0]?.name ?? null);

  isMaintenance = computed(() => this.tagsService.checkIfGameHasTag(this.maintenanceTag(), this.game()));
  isOnboardingActive = computed(() => this.uiRepo.isOnboardingActive());

  gameTag = computed<Tag | undefined>(() => {
    const name = this.firstTagName();
    return name ? this.tagsIndex().get(name) : undefined;
  });

  gameTier = computed(() => {
    return this.game().trophy?.tier;
  });

  isGameOpenByTag = computed(() => {
    const name = this.firstTagName();
    const tag = name ? this.tagsIndex().get(name) : undefined;

    return !!tag?.isGameOpen;
  });

  showTag = computed(() => {
    const name = this.firstTagName();

    if (!name) return false;

    return (
      !this.isOnboardingActive() ||
      name === this.maintenanceTag().name ||
      name === this.lockedTag().name ||
      name === this.testTag().name
    );
  });

  canShow = computed(() => {
    const game = this.game();
    return game.isUnlocked || this.isGameOpenByTag();
  });

  gameTagUrl = computed(() => {
    const tag = this.gameTag();
    return tag ? this.tagsService.setTagImage(tag, this.game()) : '';
  });

  gameIconImg = computed(() => this.assetsService.getGameIconUrlLegacy(this.game().gameId, this.gamesVersion()));

  goToGame(game: Game): void {
    this.analyticsService.logEvent('custom_game_started', game.gameId);
    this.firehoseAnalyticsService.track('ui_click_join_slot', { gameId: this.game().gameId });

    const gamePromise = this.appService.showGame(game.gameId);
    if (gamePromise)
      gamePromise
        .then(() => {
          this.firehoseAnalyticsService.track('on_slot_join', { gameId: this.game().gameId });
        })
        .catch((e) => this.logger.error(e));
  }

  unlockGame(): void {
    if (this.game().discontinuedDate > new Date()) {
      this.unlockDiscontinuedDialog();
    }
  }

  private unlockDiscontinuedDialog(): void {
    const game = this.game();
    const date = new Date(game.discontinuedDate);
    const data = {
      gameId: game.gameId,
      gameTitle: game.gameTitle,
      day: date.getDate(),
      month: date.toLocaleString(this.user().culture, { month: 'long' }),
      purchase: true,
    };

    this.eventManager.openModal(
      new Modal(NonPersistedModalName.NotifyDiscontinuedModalComponent, ModalType.View, data)
    );
  }
}
